import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography } from '@mui/material';
import { colorTokens, radiusTokens, typographyTokens } from '../../app/designTokens';
import { useAuth } from '../../auth/AuthContext';
import { DetailField } from '../../components/InfoCard';
import { LoadingSkeleton } from '../../components/LoadingSkeleton';
import { PageHeader } from '../../components/PageHeader';
import { SectionCard } from '../../components/SectionCard';
import { StatusBadge } from '../../components/StatusBadge';
import { TableToolbar } from '../../components/TableToolbar';
import { useCustomerPortal } from '../../portal/CustomerPortalContext';
import {
  formatCustomerRole,
  formatMembershipStatus,
  formatUserType,
} from '../../utils/customerPortalLabels';

const { semantic } = colorTokens;

const detailGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
  gap: 2.5,
};

const membershipCardSx = {
  p: 2,
  bgcolor: semantic.bg.input,
  border: `1px solid ${semantic.border.subtle}`,
  borderRadius: `${radiusTokens.md}px`,
  minWidth: 0,
};

const companyNameSx = {
  fontFamily: typographyTokens.fontFamily,
  fontWeight: typographyTokens.fontWeightSemibold,
  fontSize: 13,
  lineHeight: '20px',
  color: semantic.text.primary,
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
};

const roleBadgeSx = {
  display: 'inline-flex',
  alignItems: 'center',
  px: 1,
  py: 0.25,
  borderRadius: `${radiusTokens.sm}px`,
  border: `1px solid ${semantic.border.subtle}`,
  bgcolor: semantic.action.secondary.bg,
  fontFamily: typographyTokens.fontFamilyMono,
  fontSize: 11,
  lineHeight: '16px',
  color: semantic.text.secondary,
  whiteSpace: 'nowrap',
};

const mutedSx = {
  fontSize: 13,
  lineHeight: '18px',
  color: semantic.text.muted,
};

export function CustomerPortalProfilePage() {
  const { t } = useTranslation(['customers', 'common', 'customerPortal']);
  const { session } = useAuth();
  const { customers, loading: portalLoading } = useCustomerPortal();
  const emptyValue = t('common:empty_value');

  if (portalLoading) {
    return <LoadingSkeleton type="Detail" />;
  }

  const userType = session?.user_type;
  const userTypeLabel = formatUserType(userType) || emptyValue;
  const membershipCount = customers.length;

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="List"
        title={t('customers:portal.profile_title')}
        subtitle={t('customers:portal.profile_subtitle')}
      />

      <SectionCard
        variant="Default"
        title={t('customers:portal.user_section')}
        sx={{ mb: 2.5 }}
      >
        <Box sx={detailGridSx}>
          <DetailField
            label={t('common:name')}
            value={session?.user?.name || emptyValue}
          />
          <DetailField
            label={t('common:email')}
            value={session?.user?.email || emptyValue}
          />
          <DetailField label={t('customers:portal.user_type')}>
            {userType ? (
              <StatusBadge
                status={userType}
                category="generic"
                apiLabel={userTypeLabel}
                size="Sm"
                sx={{ alignSelf: 'flex-start' }}
              />
            ) : (
              emptyValue
            )}
          </DetailField>
        </Box>
      </SectionCard>

      <SectionCard variant="Default" noPadding sx={{ mb: 0, width: '100%' }}>
        <TableToolbar
          mode="Default"
          title={t('customers:portal.memberships')}
          meta={(
            <StatusBadge
              status="list"
              category="generic"
              apiLabel={t('customers:portal.memberships_toolbar_count', {
                count: membershipCount,
              })}
              size="Sm"
              tone="Neutral"
            />
          )}
        />

        <Box sx={{ px: 2.5, pt: 2, pb: 2.5 }}>
          {membershipCount === 0 ? (
            <Typography sx={mutedSx}>
              {t('customers:portal.no_memberships')}
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {customers.map((customer) => {
                const membership = session?.customers?.find((c) => c.id === customer.id);
                const membershipStatus = membership?.membership_status || customer.membership_status;
                const roles = membership?.roles || [];

                return (
                  <Box key={customer.id} sx={membershipCardSx}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1.5}
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      justifyContent="space-between"
                      sx={{ mb: 1.5 }}
                    >
                      <Typography component="span" sx={companyNameSx}>
                        {customer.name || emptyValue}
                      </Typography>
                      {membershipStatus ? (
                        <StatusBadge
                          status={membershipStatus}
                          category="membership"
                          apiLabel={formatMembershipStatus(membershipStatus)}
                          size="Sm"
                          sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, flexShrink: 0 }}
                        />
                      ) : null}
                    </Stack>

                    <DetailField label={t('customers:portal.roles_label')}>
                      {roles.length > 0 ? (
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                          {roles.map((role) => (
                            <Box
                              key={`${customer.id}-${role}`}
                              component="span"
                              sx={roleBadgeSx}
                            >
                              {formatCustomerRole(role)}
                            </Box>
                          ))}
                        </Stack>
                      ) : (
                        <Typography sx={mutedSx}>{emptyValue}</Typography>
                      )}
                    </DetailField>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>
      </SectionCard>
    </Box>
  );
}
